import { FC } from 'react';
import { format } from 'date-fns';
import { useTranslate } from 'hooks/useTranslate';
import { formatCurrencyDecimals, formatNumber } from 'utils/formats';
import { dateFormat } from 'constants/formats';
import {
  IncentiveOfferOperation,
  IncentiveOfferType,
  IncentivesMarketBeneficiary,
} from 'modules/incentivesMarket/types';
import CustomLoading from 'modules/_shared/components/CustomLoading';
import Button, { ButtonVariants } from 'modules/_shared/components/Buttons';
import { TablePagination } from 'modules/_shared/Table/components/TablePagination';

import { hasInternalView } from 'utils/showDetails';
import { statusBadgeMap } from '../../../constants/incentiveOfferOperationStatus';
import getOperationReference from '../../../utils/getOperationReference';
import { IncentiveOfferOperationsSummary } from '../types/IncentiveOfferDetailContextType';

/* eslint-disable no-unused-vars -- callback param names in type definitions */
type IncentiveOfferOperationsTableProps = {
  operations: IncentiveOfferOperation[];
  offerType: IncentiveOfferType;
  page: number;
  rowsPerPage: number;
  totalRows: number;
  summary: IncentiveOfferOperationsSummary;
  isLoading?: boolean;
  isDownloading?: boolean;
  onGoToOperation: (operationId: string) => void;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  onDownload: () => void;
};
/* eslint-enable no-unused-vars */

const getParticipantName = (
  participant: string | Partial<IncentivesMarketBeneficiary>
) => {
  if (typeof participant === 'string') return participant;
  return participant?.name || '';
};

const IncentiveOfferOperationsTable: FC<IncentiveOfferOperationsTableProps> = ({
  operations,
  offerType,
  page,
  rowsPerPage,
  totalRows,
  summary,
  isLoading = false,
  isDownloading = false,
  onGoToOperation,
  onPageChange,
  onRowsPerPageChange,
  onDownload,
}) => {
  const { t } = useTranslate();

  if (totalRows === 0 && !isLoading) return null;

  const isBuyOffer = offerType === IncentiveOfferType.BUY;
  const showPagination = totalRows > rowsPerPage;
  const showLoadingOverlay = isLoading || isDownloading;

  return (
    <div
      className="card card-bordered mb-4"
      data-testid="incentive-offer-operations-table"
    >
      <div className="card-inner">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
          <h6 className="card-title mb-0">
            <em className="icon ni ni-list-check text-primary mr-2" />
            {t('OperationStatus')}
          </h6>
          <Button
            variant={ButtonVariants.LIGHT}
            onClick={onDownload}
            disabled={showLoadingOverlay || totalRows === 0}
            title={t('DownloadList')}
            data-testid="incentive-offer-operations-download-button"
            className="mb-2 mb-md-0"
          >
            <em className="icon ni ni-download mr-1" />
            <span>{t('DownloadList')}</span>
          </Button>
        </div>

        <p className="text-soft fs-13px mb-3">
          {t('OperationsTableDescription')}
        </p>

        <div className="table-responsive">
          <table className="table table-tranx table-hover mb-0">
            <thead>
              <tr className="tb-tnx-head">
                <th className="tb-tnx-id">
                  <span className="d-none d-md-inline">
                    {t('OperationRef')}
                  </span>
                </th>
                <th>
                  <span className="d-none d-md-inline">
                    {t(isBuyOffer ? 'Seller' : 'Buyer')}
                  </span>
                </th>
                <th>
                  <span className="d-none d-md-inline">
                    {t('IncentivesMarketUnits')}
                  </span>
                </th>

                <th>
                  <span className="d-none d-md-inline">{t('Amount')}</span>
                </th>
                <th>
                  <span className="d-none d-md-inline">{t('Date')}</span>
                </th>
                <th>
                  <span className="d-none d-md-inline">{t('Status')}</span>
                </th>
                <th className="tb-tnx-action text-right" />
              </tr>
            </thead>
            <tbody>
              {operations.map((op) => {
                const badge = statusBadgeMap[op.status] || {
                  className: 'badge-secondary',
                  label: op.status,
                };

                return (
                  <tr
                    key={op._id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => onGoToOperation(op._id)}
                  >
                    <td className="tb-tnx-id">
                      <span className="ref text-base badge badge-light">
                        {getOperationReference(op)}
                      </span>
                    </td>
                    <td>
                      <span className="d-inline-flex align-items-center">
                        <em className="icon ni ni-user mr-1 text-soft" />
                        {getParticipantName(isBuyOffer ? op.seller : op.buyer)}
                      </span>
                    </td>
                    <td>{formatNumber(op.unitsCount)}</td>
                    <td>
                      <strong>{formatCurrencyDecimals(op.totalPrice)}</strong>
                    </td>
                    <td>{format(new Date(op.requestDate), dateFormat)}</td>
                    <td>
                      <span className={`badge badge-dim ${badge.className}`}>
                        {t(badge.label)}
                      </span>
                    </td>
                    <td className="tb-tnx-action text-right">
                      <button
                        type="button"
                        className="btn btn-icon btn-trigger btn-sm"
                        title={t('ViewDetail')}
                        onClick={(e) => {
                          e.stopPropagation();
                          onGoToOperation(op._id);
                        }}
                      >
                        <em className="icon ni ni-arrow-right" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {showPagination && (
          <TablePagination
            key={`${page}-${rowsPerPage}`}
            totalRows={totalRows}
            defaultPage={page}
            defaultRowsPerPage={rowsPerPage}
            onChangePage={onPageChange}
            onChangeRowsPerPage={onRowsPerPageChange}
          />
        )}

        <div className="mt-3 pt-3 border-top">
          <p className="text-soft fs-12px mb-0">
            <em className="icon ni ni-info mr-1" />
            <span>
              {t('TotalOperations')}: <strong>{totalRows}</strong> ·{' '}
            </span>
            <span>
              {t('Completed')}: {summary.completed} ·{' '}
            </span>
            <span>
              {t('Pending')}: {summary.pending} ·{' '}
            </span>
            {hasInternalView() && (
              <span>
                {t('IncentivesMarketTotalUnits')}:{' '}
                <strong>{formatNumber(summary.totalUnits)}</strong> ·{' '}
              </span>
            )}
            {hasInternalView() && (
              <span>
                {t('TotalOperatedAmount')}:{' '}
                <strong>{formatCurrencyDecimals(summary.totalAmount)}</strong>
              </span>
            )}
          </p>
        </div>
      </div>

      {showLoadingOverlay && <CustomLoading />}
    </div>
  );
};

export default IncentiveOfferOperationsTable;
